import AlertMapEditor from 'features/Weather/features/WeatherAlerts/features/AlertCreator/features/AlertMapEditor/index.web';
import useLocation from 'hooks/useLocation';
import useWindow from 'hooks/useWindow';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Group, Panel } from 'react-resizable-panels';
import { useNavigate } from 'react-router-dom';
import { useLazyGetFemaAlertsQuery } from 'services/ipaws/ipaws.api';
import { ResizablePanelHandle } from 'utils/layout/panelResize.utils';
import AlertCreatorFormPanel from './components/AlertCreatorFormPanel/AlertCreatorFormPanel';
import {
  ALERT_CREATOR_ACCESS_LEVELS,
  FEMA_PERMISSIONS,
} from './components/IpawsAlertFields/config/Ipaws.config';
import {
  selectFemaUpdateContext,
  selectFemaUpdateRequest,
  selectRescAlertDraft,
} from './state/selectors/alertCreatorSelectors';
import {
  resetIpawsCreator,
  setAlertMode,
  setFemaUpdateContext,
  setMapCenter,
  setZoom,
} from './state/slices/alertCreatorSlice';
import { ALERT_MODES } from './utils/alertCreator.utils';

const AlertCreator = ({ perms }) => {
  const rescAlertDraft = useSelector(selectRescAlertDraft);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindow();
  const isDesktop = width >= 992;
  const { locations, jurisdictionPolygons = [] } = useLocation();
  const workplaceLocation = locations.workplaceLocation;
  const hasWorkplaceJurisdiction = jurisdictionPolygons.length > 0;
  const femaUpdateContext = useSelector(selectFemaUpdateContext);
  const femaUpdateRequest = useSelector(selectFemaUpdateRequest);
  const [getFemaAlerts] = useLazyGetFemaAlertsQuery();
  const [femaUpdateLoadError, setFemaUpdateLoadError] = useState(null);
  const hasCenteredOnWorkplaceRef = useRef(false);

  useEffect(() => {
    (async () => {
      // Either right opens the creator: RescAlert alerts, or FEMA alerts (FE-01: the FEMA form is
      // never gated behind the RescAlert alert permission alone).
      const allowed =
        (await perms.validatePermissions(['Create-EditAlerts'])) ||
        (await perms.validatePermissions([FEMA_PERMISSIONS.create]));
      if (!allowed) {
        navigate('/404');
        return;
      }
    })();
  }, [navigate, perms]);

  useEffect(() => {
    if (femaUpdateContext) dispatch(setAlertMode(ALERT_MODES.IPAWS));
  }, [dispatch, femaUpdateContext]);

  useEffect(() => {
    if (!femaUpdateRequest) return undefined;

    let isCurrentRequest = true;
    setFemaUpdateLoadError(null);

    getFemaAlerts({})
      .unwrap()
      .then((records) => {
        if (!isCurrentRequest) return;
        const storedRecord = records.find(
          (record) =>
            record.payload.identifier === femaUpdateRequest.identifier &&
            record.envelope.sender === femaUpdateRequest.sender &&
            String(record.cogId) === femaUpdateRequest.cogId,
        );

        if (!storedRecord) throw new Error('The stored FEMA message was not found.');
        dispatch(setFemaUpdateContext(storedRecord));
      })
      .catch(() => {
        if (isCurrentRequest) setFemaUpdateLoadError('The FEMA alert could not be loaded.');
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [dispatch, femaUpdateRequest, getFemaAlerts]);

  // Clears the draft and any update context on the way out, so a later visit never reuses stale
  // data left over from a previous, unsubmitted alert.
  useEffect(() => {
    return () => {
      dispatch(resetIpawsCreator());
    };
  }, [dispatch]);

  // Center on the alert being edited, otherwise center once on the workplace location. Waiting for
  // the jurisdictions to load avoids centering on the global default and then jumping, and running
  // once keeps a later workplace refresh from yanking the map while the user is drawing.
  useEffect(() => {
    const updateArea = femaUpdateContext?.payload?.info?.[0]?.area?.[0];
    const updateCenter = updateArea?.circle?.[0]?.center
      ? {
          lat: updateArea.circle[0].center.latitude,
          lng: updateArea.circle[0].center.longitude,
        }
      : updateArea?.polygon?.[0]?.[0]
        ? {
            lat: updateArea.polygon[0][0].latitude,
            lng: updateArea.polygon[0][0].longitude,
          }
        : null;

    if (updateCenter && !hasCenteredOnWorkplaceRef.current) {
      hasCenteredOnWorkplaceRef.current = true;
      dispatch(setMapCenter(updateCenter));
      dispatch(setZoom(14));
      return;
    }

    if (femaUpdateContext) return;

    if (rescAlertDraft?.mapCenter) {
      dispatch(setMapCenter(rescAlertDraft.mapCenter));
      dispatch(setZoom(14));
      return;
    }

    if (workplaceLocation.isLoading || hasCenteredOnWorkplaceRef.current) return;

    hasCenteredOnWorkplaceRef.current = true;
    // The map editor fits the whole jurisdiction when it opens (card FE-05). A zoom-14 center set
    // here after that fit would zoom the map in again, so it is used only without a jurisdiction.
    if (hasWorkplaceJurisdiction) return;
    dispatch(setMapCenter(workplaceLocation.coords));
    dispatch(setZoom(14));
  }, [
    femaUpdateContext,
    hasWorkplaceJurisdiction,
    rescAlertDraft.mapCenter,
    workplaceLocation,
    dispatch,
  ]);

  const canUseCreator =
    perms.testPermission(ALERT_CREATOR_ACCESS_LEVELS, ['Create-EditAlerts']) ||
    perms.testPermission(ALERT_CREATOR_ACCESS_LEVELS, [FEMA_PERMISSIONS.create]);
  if (!canUseCreator) return null;

  if (femaUpdateRequest && !femaUpdateContext) {
    return (
      <Container
        fluid
        className="alert-creator-page d-flex flex-column align-items-center justify-content-center gap-3 py-3"
      >
        {femaUpdateLoadError ? (
          <Alert variant="danger" className="text-center">
            <Alert.Heading>Unable to edit FEMA alert</Alert.Heading>
            <p>{femaUpdateLoadError}</p>
            <Button
              type="button"
              variant="outline-danger"
              onClick={() => navigate('/publicuser/alerts')}
            >
              Back to Active Alerts
            </Button>
          </Alert>
        ) : (
          <div className="d-flex flex-column align-items-center gap-3" role="status">
            <Spinner animation="border" aria-hidden="true" />
            <span>Loading FEMA alert...</span>
          </div>
        )}
      </Container>
    );
  }

  return (
    <Container fluid className="alert-creator-page d-flex flex-column py-3">
      <header className="alert-creator-page-header">
        <h1 className="alert-creator-page-title">Alert Creator</h1>
      </header>

      {!isDesktop ? (
        <div className="alert-creator-mobile-layout d-flex flex-column gap-3 align-items-stretch">
          <div className="alert-creator-map-panel flex-shrink-0">
            <AlertMapEditor />
          </div>
          <AlertCreatorFormPanel />
        </div>
      ) : (
        <Group
          className="alert-creator-workspace d-flex flex-row align-items-stretch flex-grow-1 overflow-hidden min-h-0 h-100"
          orientation="horizontal"
        >
          <Panel
            defaultSize={58}
            minSize={35}
            className="alert-creator-map-panel d-flex flex-column h-100 min-h-0"
          >
            <AlertMapEditor fillContainer />
          </Panel>
          <ResizablePanelHandle direction="vertical" />
          <Panel
            defaultSize={42}
            minSize={30}
            className="alert-creator-form-panel d-flex flex-column h-100 min-h-0"
          >
            <AlertCreatorFormPanel />
          </Panel>
        </Group>
      )}
    </Container>
  );
};

export default AlertCreator;
